import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  UiQuestionnaireLayout,
  Col,
  UiText,
  Row,
  UiButton,
  UiBox,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiInput,
} from '~/components/elements';
import {UiCard} from '~/components/elements/UiCard';
import {AT_HOME, MENOPAUSAL_PROFILE_TEST_KEYS} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer, setNextQuestionnaire} from '~/redux/questionnaires/slice';
import {Questionnaire, Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire4Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === MENOPAUSAL_PROFILE_TEST_KEYS.APPOINTMENT_PREFERENCES);
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[MENOPAUSAL_PROFILE_TEST_KEYS.APPOINTMENT_PREFERENCES]?.data,
  );
  const selectedTimes = currentAnswer?.selectedTimes || [];
  const [selectedDays, setSelectedDays] = useState<Questionnaire['dayOptions']>(
    questionnaire?.dayOptions?.map(day => {
      const dayIsSelected = selectedTimes?.some((selectedDay: any) => selectedDay.day === day.value);
      return {
        ...day,
        selected: dayIsSelected,
        timeOptions: day.timeOptions.map(time => {
          const timeIsSelected = selectedTimes?.some((selectedDay: any) => {
            return selectedDay.day === day.value && selectedDay.selectedTimes.includes(time.value);
          });
          return {
            ...time,
            selected: timeIsSelected,
          };
        }),
      };
    }) as Questionnaire['dayOptions'],
  );
  const rules = {
    address: {
      presence: {allowEmpty: false},
    },
    unitNumber: {
      presence: {allowEmpty: false},
    },
    postalCode: {
      presence: {allowEmpty: false},
    },
    specialInstruction: {
      presence: {allowEmpty: false},
    },
  };
  const {inputs, setInputs, validation} = useInput(
    {
      clinicId: currentAnswer?.clinicId || null,
      selectedOption: currentAnswer?.selectedOption || '',
      address: currentAnswer?.address || '',
      postalCode: currentAnswer?.postalCode || '',
      specialInstruction: currentAnswer?.specialInstruction || '',
      unitNumber: currentAnswer?.unitNumber || '',
    },
    {rules},
  );
  const filteredSelectedDays = selectedDays?.filter(day => day.selected);

  const isDisable =
    !inputs?.selectedOption ||
    !filteredSelectedDays?.length ||
    filteredSelectedDays?.some(val => val.timeOptions.every(el => !el.selected));

  const onClickSubmit = () => {
    if (inputs.selectedOption === AT_HOME) {
      if (validation.getErrors()) {
        return;
      }
    }
    const selectedDayValues = filteredSelectedDays?.map(({value: dayValue}) => ({day: dayValue}));
    const selectedTimeValues = filteredSelectedDays?.map(({value: dayValue, dayOfWeek, timeOptions}) => ({
      day: dayValue,
      dayOfWeek,
      selectedTimes: timeOptions?.filter(time => time?.selected)?.map(({value: timeValue}) => timeValue),
    }));

    dispatch(
      setQuestionnaireAnswer({
        key: MENOPAUSAL_PROFILE_TEST_KEYS.APPOINTMENT_PREFERENCES,
        index: questionnaire?.index || '',
        data: {
          ...inputs,
          selectedDays: selectedDayValues,
          selectedTimes: selectedTimeValues,
        },
      }),
    );
    return dispatch(setNextQuestionnaire({value: MENOPAUSAL_PROFILE_TEST_KEYS.YOUR_TEST_PACKAGE}));
  };

  const handleSelectedDay = (day: any, dayIndex: number) => {
    setSelectedDays(prevDays => {
      const newDays = [...prevDays];
      newDays[dayIndex] = {...day, selected: !day.selected};
      return newDays;
    });
  };

  const handleSelectedTime = (dayIndex: number, timeIndex: number) => {
    setSelectedDays(prevDays => {
      const newDays = [...prevDays];
      newDays[dayIndex] = {
        ...newDays[dayIndex],
        timeOptions: newDays[dayIndex].timeOptions.map((time: any, index: number) =>
          index === timeIndex ? {...time, selected: !time.selected} : time,
        ),
      };
      return newDays;
    });
  };
  const renderLocation = () => {
    if (inputs.selectedOption === AT_HOME) {
      const selectedOption = questionnaire?.options?.find(option => option.value === inputs.selectedOption);
      return (
        <UiBox w="100%" mt={spacing.small} className="animate__animated animate__fadeInLeft">
          {selectedOption?.relations?.map((val: Relation) => (
            <UiBox key={val?.questions?.default}>
              <UiText fontSize="md" fontWeight="bold" mt={spacing.tiny}>
                {val?.questions?.default}
              </UiText>
              <UiCard variant="rounded" width="100%" mt={spacing.small} mb={spacing.medium}>
                <UiFormControl id="address" isRequired isInvalid={!!validation?.errors?.address}>
                  <UiFormLabel fontWeight="400" fontSize="xs">
                    Address
                  </UiFormLabel>
                  <UiInput
                    placeholder="Your Address..."
                    variant="flushed"
                    defaultValue={inputs.address}
                    onChange={({target}) => setInputs({address: target.value})}
                    fontWeight="600"
                    fontSize="sm"
                  />
                  {validation?.errors?.address && (
                    <UiFormErrorMessage>{validation?.errors?.address[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
                <UiFormControl id="unitNumber" isRequired isInvalid={!!validation?.errors?.unitNumber}>
                  <UiFormLabel fontWeight="400" fontSize="xs">
                    Unit Number
                  </UiFormLabel>
                  <UiInput
                    placeholder="#04 - 88"
                    variant="flushed"
                    defaultValue={inputs.unitNumber}
                    onChange={({target}) => setInputs({unitNumber: target.value})}
                    fontWeight="600"
                    fontSize="sm"
                  />
                  {validation?.errors?.unitNumber && (
                    <UiFormErrorMessage>{validation?.errors?.unitNumber[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
                <UiFormControl id="postalCode" isRequired isInvalid={!!validation?.errors?.postalCode}>
                  <UiFormLabel fontWeight="400" fontSize="xs">
                    Post Code
                  </UiFormLabel>
                  <UiInput
                    placeholder="099785"
                    variant="flushed"
                    defaultValue={inputs.postalCode}
                    onChange={({target}) => setInputs({postalCode: target.value})}
                    fontWeight="600"
                    fontSize="sm"
                  />
                  {validation?.errors?.postalCode && (
                    <UiFormErrorMessage>{validation?.errors?.postalCode[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
                <UiFormControl id="specialInstruction" isRequired isInvalid={!!validation?.errors?.specialInstruction}>
                  <UiFormLabel fontWeight="400" fontSize="xs">
                    Special Instructions
                  </UiFormLabel>
                  <UiInput
                    placeholder="Call me before you arrive"
                    variant="flushed"
                    defaultValue={inputs.specialInstruction}
                    onChange={({target}) => setInputs({specialInstruction: target.value})}
                    fontWeight="600"
                    fontSize="sm"
                  />
                  {validation?.errors?.specialInstruction && (
                    <UiFormErrorMessage>{validation?.errors?.specialInstruction[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
              </UiCard>
            </UiBox>
          ))}
        </UiBox>
      );
    }
    return null;
  };
  const renderTimes = (value: any, dayIndex: number, isSelected?: boolean) => {
    if (isSelected) {
      return (
        <Row key={value.label} align="center" justifyContent="center" w="100%" mb={5}>
          <UiText w="60px" fontWeight={600} fontSize={16} color="fadedJade">
            {value.label.substring(0, 3)}
          </UiText>
          <Row flexWrap={'wrap'}>
            {value.timeOptions.map((timeOption: any, timeIndex: number) => (
              <UiButton
                w={{base: '85px', md: '120px'}}
                fontSize={{base: '12px', md: '14px'}}
                onClick={() => handleSelectedTime(dayIndex, timeIndex)}
                key={timeOption.label}
                title={timeOption.label}
                variant={timeOption.selected ? 'solid' : 'outlinePrimary'}
                mr={2}
                mb={2}
                minW="48px"
              />
            ))}
          </Row>
        </Row>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      disableNext={isDisable}
    >
      <Col align="center" textAlign="center">
        <UiText variant="mdqTitle" mt={10} my={5}>
          {questionnaire?.questions?.default}
        </UiText>
        {questionnaire?.descriptions?.default?.map(desc => (
          <UiText key={desc} fontWeight={400} fontSize={14}>
            {desc}
          </UiText>
        ))}
        <UiText fontWeight={600} fontSize={14} my={5}>
          {questionnaire?.questions?.sub}
        </UiText>
        <Row justify="center" w="100%">
          {questionnaire?.options?.map(value => (
            <UiButton
              onClick={() => setInputs({clinicId: value?.clinicId, selectedOption: value?.value})}
              key={`${value.label}-${value.value}`}
              title={value?.label}
              mb={4}
              variant={inputs?.selectedOption === value.value ? 'solid' : 'outlinePrimary'}
              ml={2}
            />
          ))}
        </Row>
        {renderLocation()}
        <UiText fontWeight={600} fontSize={14} my={5}>
          {questionnaire?.questions?.dayQuestion}
        </UiText>
        <Row justify="center" flexWrap="wrap" w="100%">
          {selectedDays.map((day: any, index) => (
            <UiButton
              onClick={() => handleSelectedDay(day, index)}
              key={day.label}
              title={day.label.substring(0, 3)}
              variant={day.selected ? 'solid' : 'outlinePrimary'}
              mr={2}
              w={{base: '70px', md: '120px'}}
              fontSize={{base: '12px', md: '14px'}}
              mb={5}
              minW="48px"
            />
          ))}
        </Row>
        <UiText fontWeight={600} fontSize={14} my={5}>
          {questionnaire?.questions?.timeQuestion}
        </UiText>
        {selectedDays.map((day, dayIndex) => renderTimes(day, dayIndex, day?.selected))}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire4Screen;
